/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: GatewayWizardModal.tsx
 🗓️ Created: 2026-01-19
 🧩 Description:
    Wizard modal for quickly configuring gateways using
    pre-defined VoIP provider templates.
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, ChevronLeft, Check, ExternalLink, Search } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { PROVIDER_TEMPLATES, applyProviderTemplate, ProviderTemplate, ProviderField } from '../utils/providerTemplates';
import { SecretInput } from '../../../components/ui/SecretField';
import { GatewayFormState, FieldValue } from '../types';

interface GatewayWizardModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (config: Partial<GatewayFormState>) => void;
}

type WizardStep = 'provider' | 'credentials' | 'preview';

export default function GatewayWizardModal({ isOpen, onClose, onApply }: GatewayWizardModalProps) {
    const { t } = useI18n();

    const [step, setStep] = useState<WizardStep>('provider');
    const [selectedProvider, setSelectedProvider] = useState<ProviderTemplate | null>(null);
    const [userInputs, setUserInputs] = useState<Record<string, string>>({});
    const [searchQuery, setSearchQuery] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});


    // Filter and sort providers alphabetically (Custom Provider always last)
    const filteredProviders = useMemo(() => {
        let providers = [...PROVIDER_TEMPLATES];

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            providers = providers.filter(p =>
                p.name.toLowerCase().includes(query) ||
                p.description.toLowerCase().includes(query)
            );
        }

        // Sort alphabetically, keeping "Custom Provider" at the end
        return providers.sort((a, b) => {
            if (a.id === 'custom') return 1;
            if (b.id === 'custom') return -1;
            return a.name.localeCompare(b.name);
        });
    }, [searchQuery]);

    // Generate preview config
    const previewConfig = useMemo(() => {
        if (!selectedProvider) return null;
        return applyProviderTemplate(selectedProvider, userInputs);
    }, [selectedProvider, userInputs]);

    const handleProviderSelect = useCallback((provider: ProviderTemplate) => {
        setSelectedProvider(provider);
        setUserInputs({});
        setErrors({});
        setStep('credentials');
    }, []);

    const handleInputChange = useCallback((fieldName: string, value: string) => {
        setUserInputs(prev => ({ ...prev, [fieldName]: value }));
        // Clear error when user types
        if (errors[fieldName]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[fieldName];
                return next;
            });
        }
    }, [errors]);

    const validateFields = useCallback((): boolean => {
        if (!selectedProvider) return false;

        const newErrors: Record<string, string> = {};
        for (const field of selectedProvider.fields) {
            if (field.required && !userInputs[field.name]?.trim()) {
                newErrors[field.name] = t('common.required') || 'Required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [selectedProvider, userInputs, t]);

    const handleNext = useCallback(() => {
        if (step === 'credentials') {
            if (validateFields()) {
                setStep('preview');
            }
        }
    }, [step, validateFields]);

    const handleBack = useCallback(() => {
        if (step === 'credentials') {
            setStep('provider');
            setSelectedProvider(null);
        } else if (step === 'preview') {
            setStep('credentials');
        }
    }, [step]);

    const handleApply = useCallback(() => {
        if (!selectedProvider || !previewConfig) return;

        const formConfig: Partial<GatewayFormState> = {
            name: `${selectedProvider.name.replace(/\s+/g, '_').replace(/[()]/g, '')}_Gateway`,
            description: selectedProvider.description,
            enabled: true,
            settings: {
                username: String(previewConfig.username || ''),
                password: String(previewConfig.password || ''),
                proxy: String(previewConfig.proxy || ''),
                realm: String(previewConfig.realm || previewConfig.proxy || ''),
                register: Boolean(previewConfig.register),
                registerTransport: String(previewConfig.registerTransport || 'udp'),
                expireSeconds: Number(previewConfig.expireSeconds || 600),
                retrySeconds: Number(previewConfig.retrySeconds || 30),
                context: String(previewConfig.context || 'public'),
                fromUser: String(previewConfig.fromUser || ''),
                fromDomain: String(previewConfig.fromDomain || '')
            } as Record<string, FieldValue>
        };

        onApply(formConfig);
        handleClose();
    }, [selectedProvider, previewConfig, onApply]);

    const handleClose = useCallback(() => {
        setStep('provider');
        setSelectedProvider(null);
        setUserInputs({});
        setSearchQuery('');
        setErrors({});
        onClose();
    }, [onClose]);

    const renderField = useCallback((field: ProviderField) => {
        const value = userInputs[field.name] || '';
        const error = errors[field.name];

        if (field.type === 'select') {
            return (
                <div key={field.name} className="space-y-1">
                    <label className="block text-sm font-medium text-foreground">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <select
                        value={value}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        className={`w-full px-3 py-2 text-sm border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${error ? 'border-red-500' : 'border-border'
                            }`}
                    >
                        <option value="">{t('common.select') || 'Select...'}</option>
                        {field.options?.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    {error && <p className="text-xs text-red-500">{error}</p>}
                    {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
                </div>
            );
        }

        if (field.type === 'password') {
            return (
                <div key={field.name} className="space-y-1">
                    <label className="block text-sm font-medium text-foreground">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <SecretInput
                        value={value}
                        onChange={(val) => handleInputChange(field.name, val)}
                        placeholder={field.placeholder}
                        className={error ? 'border-red-500' : ''}
                        showCopy
                    />
                    {error && <p className="text-xs text-red-500">{error}</p>}
                    {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
                </div>
            );
        }

        return (
            <div key={field.name} className="space-y-1">
                <label className="block text-sm font-medium text-foreground">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                    type="text"
                    value={value}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    className={`w-full px-3 py-2 text-sm border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${error ? 'border-red-500' : 'border-border'
                        }`}
                />
                {error && <p className="text-xs text-red-500">{error}</p>}
                {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
            </div>
        );
    }, [userInputs, errors, handleInputChange, t]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 99999 }}>
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/30">
                    <div>
                        <h2 className="text-lg font-semibold text-foreground">
                            {t('gateways.wizard.title') || 'Gateway Provider Wizard'}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            {step === 'provider' && (t('gateways.wizard.selectProvider') || 'Select your VoIP provider')}
                            {step === 'credentials' && selectedProvider && `${selectedProvider.name} - ${t('gateways.wizard.enterCredentials') || 'Enter credentials'}`}
                            {step === 'preview' && (t('gateways.wizard.preview') || 'Review configuration')}
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Step 1: Provider Selection */}
                    {step === 'provider' && (
                        <div className="space-y-4">
                            {/* Search */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder={t('gateways.wizard.searchProviders') || 'Search providers...'}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                />
                            </div>

                            {/* Provider Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {filteredProviders.map(provider => (
                                    <button
                                        key={provider.id}
                                        onClick={() => handleProviderSelect(provider)}
                                        className="flex flex-col items-start p-4 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left group"
                                    >
                                        <div className="flex items-center justify-between w-full mb-2">
                                            <span className="font-medium text-foreground group-hover:text-primary">
                                                {provider.name}
                                            </span>
                                            {provider.website && (
                                                <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                            )}
                                        </div>
                                        <span className="text-xs text-muted-foreground line-clamp-2">
                                            {provider.description}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            {filteredProviders.length === 0 && (
                                <div className="text-center py-8 text-muted-foreground">
                                    {t('common.noResults') || 'No providers found'}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 2: Credentials */}
                    {step === 'credentials' && selectedProvider && (
                        <div className="space-y-6">
                            <div className="p-4 bg-muted/30 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-medium text-foreground">{selectedProvider.name}</h3>
                                        <p className="text-sm text-muted-foreground">{selectedProvider.description}</p>
                                    </div>
                                    {selectedProvider.website && (
                                        <a
                                            href={selectedProvider.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary hover:underline text-sm flex items-center gap-1"
                                        >
                                            {t('common.website') || 'Website'}
                                            <ExternalLink className="h-3 w-3" />
                                        </a>
                                    )}
                                </div>
                                {selectedProvider.notes && (
                                    <p className="mt-2 text-xs text-muted-foreground border-t border-border pt-2">
                                        💡 {selectedProvider.notes}
                                    </p>
                                )}
                            </div>

                            <div className="space-y-4">
                                {selectedProvider.fields.map(field => renderField(field))}
                            </div>
                        </div>
                    )}

                    {/* Step 3: Preview */}
                    {step === 'preview' && selectedProvider && previewConfig && (
                        <div className="space-y-4">
                            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                                <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                                    <Check className="h-5 w-5" />
                                    <span className="font-medium">{t('gateways.wizard.readyToApply') || 'Ready to apply!'}</span>
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {t('gateways.wizard.reviewBeforeApply') || 'Review the configuration below before applying to the form.'}
                                </p>
                            </div>

                            <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                                <h4 className="font-medium text-foreground border-b border-border pb-2">
                                    {t('gateways.wizard.generatedConfig') || 'Generated Configuration'}
                                </h4>

                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">{t('gateways.name') || 'Name'}:</span>
                                        <span className="ml-2 text-foreground">{selectedProvider.name.replace(/\s+/g, '_').replace(/[()]/g, '')}_Gateway</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">{t('gateways.proxy') || 'Proxy'}:</span>
                                        <span className="ml-2 text-foreground font-mono text-xs">{previewConfig.proxy}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">{t('gateways.username') || 'Username'}:</span>
                                        <span className="ml-2 text-foreground">{previewConfig.username}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">{t('gateways.register') || 'Register'}:</span>
                                        <span className="ml-2 text-foreground">{previewConfig.register ? 'Yes' : 'No'}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">{t('gateways.transport') || 'Transport'}:</span>
                                        <span className="ml-2 text-foreground uppercase">{previewConfig.registerTransport}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">{t('gateways.expireSeconds') || 'Expire'}:</span>
                                        <span className="ml-2 text-foreground">{previewConfig.expireSeconds}s</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/30">
                    <button
                        type="button"
                        onClick={step === 'provider' ? handleClose : handleBack}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                        {step !== 'provider' && <ChevronLeft className="h-4 w-4" />}
                        {step === 'provider' ? (t('common.cancel') || 'Cancel') : (t('common.back') || 'Back')}
                    </button>

                    {step === 'credentials' && (
                        <button
                            type="button"
                            onClick={handleNext}
                            className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                        >
                            {t('common.next') || 'Next'}
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    )}

                    {step === 'preview' && (
                        <button
                            type="button"
                            onClick={handleApply}
                            className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            <Check className="h-4 w-4" />
                            {t('gateways.wizard.applyToForm') || 'Apply to Form'}
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
